import { Link } from "react-router-dom";
import _ from "lodash";
import { DateTime } from "luxon";
import moment from "moment";

import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { List } from "@/Common/Components/List/List";
import { Popup } from "@/Common/Components/Modals/Popup";
import { ContentWithOverflowTooltip, IContentWithOverflowTooltipContentProps } from "@/Common/Components/Tooltip/ContentWithOverflowTooltip";
import { Tooltip } from "@/Common/Components/Tooltip/Tooltip";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { PatientMedicationView } from "@/Patient/Components/Medication/PatientMedicationView";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { PatientPrescriptionListItem } from "@/Patient/Models/Medication/PatientPrescriptionListItem";
import { PrescriptionPrintHistory } from "@/Patient/Models/Medication/PrescriptionPrintHistory";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IMedicationTabModel } from "@/Patient/Models/UI/Medication/IMedicationTabModel";
import { LongTermMedicationIcon } from "../Medication/LongTermMedicationIcon";
import { PatientMedicationGrid } from "./PatientMedicationGrid";

import styles from "./PatientMedicationTab.module.scss";

import img_icon_addfavorite from "@/Globals/_images/icons/AddFavorite.png";
import img_icon_favorite from "@/Globals/_images/icons/Favorite.png";

interface IPatientMedicationTabProps {
    PatientContactId: string;
    MedicalSummaryModel: IPatientMedicalSummaryModel;
    Model: IMedicationTabModel;
}

class PatientMedicationTabState {
    public isNewMedication: boolean;
    public favoritePrescriptions: Immutable.List<MedicationModel>;
    public hasValidMedicationName: boolean;

    public constructor(init?: Partial<PatientMedicationTabState>) {
        Object.assign(this, init);
    }

    public static getDefault(): PatientMedicationTabState {
        return new PatientMedicationTabState({
            isNewMedication: false,
            hasValidMedicationName: false,
            favoritePrescriptions: Immutable.List(CommonHelper.getCloneDeep(cacheStore.FavoritePrescriptions.toArray())) ?? Immutable.List([])
        });
    }
}

function PrintHistory(printHistory: PrescriptionPrintHistory, childProps?: IContentWithOverflowTooltipContentProps): JSX.Element {
    const localDate = DateTime.fromISO(printHistory.PrintDate.toString()).toLocal().toFormat("yyyy/MM/dd HH:mm:ss");
    const employeeName = Employee.getFullNameWithPrefixContactFacet(printHistory.EmployeeId);

    return (
        <small {...childProps} className={`${styles.printHistoryPrintedOnBy} ${styles.printedOnBy}`}>
            {cacheStore.GetString("PrintedOnByLabel", { localDate, employeeName })}
        </small>
    );
}

class PatientMedicationTab extends BaseReactComponent<IPatientMedicationTabProps, PatientMedicationTabState> {
    public constructor(props: IPatientMedicationTabProps) {
        super(props);

        this.state = PatientMedicationTabState.getDefault();
    }

    public render(): JSX.Element {
        return (
            <div className="medical-summary-container">
                {this.props.Model.get("ShowUpdateMedicationPopup") &&
                    this.renderUpdateMedicationPopup()
                }

                <div className="tb medical-summary">
                    {patientStore.PatientNoteDisabled != true &&
                        this.leftSide(this.props.Model)
                    }

                    {patientStore.PatientNoteDisabled != true &&
                        this.rightSide()
                    }
                </div>
            </div>
        );
    }

    private renderUpdateMedicationPopup(): JSX.Element {
        const medication = this.props.Model.get("MedicationModel");
        const preSaveEventId = "updateMedication_" + medication.Model.Id;

        return (
            <Popup
                title={cacheStore.GetString(this.state.isNewMedication ? "AddNewMedicationLabel" : "AdjustMedicationLabel")}
                closeTarget={LocalPatientOperations.MedicationTab.UpdateMedicationPopup_Close}
                headerClass={styles.medicationPopupHeader}
                centeredHorizontally
                draggable
                manageOverlay
                isOverflowVisible
                width={800}
            >
                <PatientMedicationView
                    baseMedication={medication}
                    actions={this.props.Model.get("UpdateMedicationStatuses")}
                    defaultSelectedActionCode={this.props.Model.get("DefaultMedicationAction")}
                    saveEventDispatchComponentId={preSaveEventId}
                    onMedicationNameValidated={isValid => this.mutateState(s => s.hasValidMedicationName = isValid)}
                    useDefaultLongTermValueAndDuration={cacheStore.ClinicSettings.EnableLongTermMedication && this.state.isNewMedication}
                />
                <div className="p-footer">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 columns text-right">
                            <div style={{ paddingTop: "10px" }}>
                                <ButtonControl
                                    className="btn btn-default push-right-5"
                                    label={cacheStore.GetString("CancelLabel")}
                                    onClick={() => {
                                        sessionStore.dispatch(LocalPatientOperations.MedicationTab.UpdateMedicationPopup_Close);
                                        this.mutateState(s => s.isNewMedication = false);
                                    }}
                                />
                                <ButtonControl
                                    className="btn btn-primary"
                                    label={cacheStore.GetString(this.state.isNewMedication ? "SaveLabel" : "ContinueLabel")}
                                    id={preSaveEventId}
                                    disabled={!this.state.hasValidMedicationName}
                                    dataCy="medicationsTab-saveMedicationButton"
                                    onClick={() => {
                                        this.mutateState(s => s.isNewMedication = false);
                                        const event = new LocalEvent(LocalPatientOperations.MedicationTab.UpdateMedicationPopup_PreSave);
                                        event.AddItem("id", preSaveEventId);
                                        MiscDispatcher.dispatch(event);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>
        );
    }

    private leftSide(model: IMedicationTabModel): JSX.Element {
        const uiModel: IPatientUIModel = patientStore.PatientUIModels[this.props.PatientContactId];
        const navigationOrigin = uiModel.get("MedicationViewOrigin");
        const orderToNavigate = `/orders/${pharmacyOrdersStore.currentPharmacyOrderId ?? ""}`;

        const arrowSvg = (
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="push-right-5">
                <path d="M5.2848 10.375L1.17969 6L5.2848 1.625" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
        return (
            <div className="fxheight tbc full-border left-primary">
                <div className="medication-return-button-container">
                    {navigationOrigin === MedicalItemViewOrigin.DocumentsTab && (
                        <button className="btn btn-primary medical-item-go-back-link" onClick={() => this.backToDocumentsClick()}>
                            {arrowSvg}
                            <span>{cacheStore.GetString("MedicalItem_BackToDocumentsLabel")}</span>
                        </button>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.PatientDocumentsTab && (
                        <button className="btn btn-primary medical-item-go-back-link" onClick={() => this.backToPatientDocumentsClick()}>
                            {arrowSvg}
                            <span>{cacheStore.GetString("MedicalItem_BackToDocumentsLabel")}</span>
                        </button>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.Note && (
                        <button className="btn btn-primary medical-item-go-back-link" onClick={this.backToConsultationClick.bind(this)}>
                            {arrowSvg}
                            <span>{cacheStore.GetString("BackToConsultationLabel")}</span>
                        </button>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.Summary && (
                        <button className="btn btn-primary medical-item-go-back-link" onClick={this.backToSummaryClick.bind(this)}>
                            {arrowSvg}
                            <span>{cacheStore.GetString("lblBackToSummary")}</span>
                        </button>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.PatientLabResultTab && (
                        <div>
                            <button className="btn btn-primary medical-item-go-back-link" onClick={this.backToSummaryPatientResult.bind(this)}>
                                {arrowSvg}
                                <span>{cacheStore.GetString("BackToResultLabel")}</span>
                            </button>
                        </div>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.ResultTab && (
                        <div>
                            <Link className="btn btn-primary medical-item-go-back-link" to="/results">
                                {arrowSvg}
                                <span>{cacheStore.GetString("BackToResultLabel")}</span>
                            </Link>
                        </div>
                    )}
                    {navigationOrigin === MedicalItemViewOrigin.PharmacyOrders && (
                        <Link className="btn btn-primary medical-item-go-back-link" to={orderToNavigate}>
                            {arrowSvg}
                            <span>{cacheStore.GetString("PharmacyOrders_BackToOrdersLabel")}</span>
                        </Link>
                    )}
                </div>
                <div className="note-search">
                    <div className="header" />
                    <div className="search-field">
                        <div className="gf">
                            <span className="glyphicon glyphicon-search" />
                        </div>
                        <TextboxControl debounceMs={400} value={model.get("SearchPrescriptionTextBox")} className="fullWidth" operation={LocalPatientOperations.MedicationTab.SearchPrescriptionTextBoxChange} placeholder={cacheStore.GetString("SearchLabel")} />
                    </div>

                    <div className="clearfix" style={{ marginTop: "15px" }} />
                </div>
                {this.prescriptionList(this.props.MedicalSummaryModel)}
            </div>
        );
    }

    private rightSide(): JSX.Element {
        const encounterIdContext = patientStore.PatientUIModels[this.props.PatientContactId].get("EditEncounterIdContext");
        const showAddNewMedicationButton = encounterIdContext || cacheStore.ClinicSettings.EnableDirectPatientProfileEdits;

        var result: JSX.Element = (
            <div className="tbc right-primary">
                <div className="summary-container">
                    <div>
                        <PatientMedicationGrid PatientContactId={this.props.PatientContactId} Summary={this.props.MedicalSummaryModel} />
                    </div>

                    <div className="meta-footer" style={{ "height": "45px", "padding": "6px 10px 10px 0px" }}>
                        <div className="flex-container" style={{ justifyContent: "space-between" }}>
                            <div />
                            <div>
                                <a onClick={this.openVigilanceAnalysis.bind(this)}>{cacheStore.GetString("AnalyseLabel")}</a>
                            </div>
                            <div>
                                {showAddNewMedicationButton && (
                                    <ButtonControl
                                        onClick={() => this.mutateState(
                                            s => s.isNewMedication = true,
                                            () => sessionStore.dispatch(LocalPatientOperations.MedicationTab.UpdateMedicationPopup_Open)
                                        )}
                                        label={cacheStore.GetString("AddNewMedicationLabel")}
                                        className="btn btn-primary pull-right"
                                        dataCy="medicationsTab-addNewMedicationButton"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );

        return result;
    }

    protected backToSummaryClick(): void {
        const operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToSummary);
        LocalEventDispatcher.dispatch(operation);
    }

    protected backToSummaryPatientResult(): void {
        const operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToPatientResults);
        LocalEventDispatcher.dispatch(operation);
    }

    protected backToConsultationClick(): void {
        const operation = new LocalEvent(LocalPatientOperations.MedicalItems.ReturnToNote);
        LocalEventDispatcher.dispatch(operation);
    }

    protected backToDocumentsClick(): void {
        sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, { value: PatientTab.MedicalSummary });
        RouterHistory.push("documents");
    }

    protected backToPatientDocumentsClick(): void {
        sessionStore.dispatch(LocalPatientOperations.UI.ChangeVerticalTabs, { value: PatientTab.Documents });
    }

    private prescriptionList(MedicalSummaryModel: IPatientMedicalSummaryModel): JSX.Element {
        var prescriptionList = (MedicalSummaryModel?.get("Prescriptions").toArray() ?? []).map(x => {
            var tmpListItem = new PatientPrescriptionListItem(x);
            tmpListItem.Name = moment(x.PrescriptionDate).format("YYYY/MM/DD");
            tmpListItem.PrescriptionDate = moment(x.PrescriptionDate).toDate();

            if (x.PrintHistory?.length) {
                x.PrintHistory = _.orderBy(x.PrintHistory, history => moment(history.PrintDate).toDate(), "desc");
            }

            return tmpListItem;
        });

        var searchText = this.props.Model.get("SearchPrescriptionTextBox");
        if (searchText != "") {
            prescriptionList = prescriptionList.filterSearchAdvanced(
                searchText,
                (IsMatchingSearchTextFct, item: PatientPrescriptionListItem) => item.Model.Medications
                    .some(med => IsMatchingSearchTextFct(med.GetName(true)))
            );
        }

        prescriptionList = prescriptionList.sortByPropertyDesc(x => x.PrescriptionDate);

        var FavPrescriptionIds = this.state.favoritePrescriptions.filter(p => p.Model.CreatorEmployeeId != null).map(m => m.Model.EMRTemplatePrescriptionId).filter(y => y != null && y != undefined).toArray();
        var uniquePrescriptionIds = ArrayHelper.Unique(FavPrescriptionIds);

        var prescriptionRender: JSX.Element[] = [];
        prescriptionList.forEach((prescription: PatientPrescriptionListItem, idx: number) => {
            const isFavorite = uniquePrescriptionIds.some(p => p == prescription.Model.PrescriptionId) == true;
            const isYourOwnWithNewMeds = prescription.Model.PrecribedByEmployee.ContactFacet_id == sessionStore.getCurrentEmployee().ContactFacetId && !prescription.Model.HasOnlyCeasedMeds;
            const providerName = `${prescription.Model.PrecribedByEmployee.FirstName} ${prescription.Model.PrecribedByEmployee.LastName}`;

            let medications: JSX.Element[] = [];
            prescription.Model.Medications.forEach((z, key) => {
                var id = "med_" + z.Model.Id + "pres_" + z.Model.PrescriptionId + key;

                if (z.isCeasedMedInSpecificPrescription(prescription.Model.PrescriptionId, prescription.PrescriptionDate)) {
                    medications.push(
                        <div key={id} className="flex-display flex-col-gap-xs red-text">
                            {z.isLongTerm() && <LongTermMedicationIcon />} {z.GetNameWithStatus(prescription.Model.PrescriptionId, prescription.PrescriptionDate)}
                        </div>
                    );
                }
                else {
                    medications.push(
                        <div key={id} className="flex-display flex-col-gap-xs">
                            {z.isLongTerm() && <LongTermMedicationIcon />} {z.GetName(false)}
                        </div>
                    );
                    if (z.PrescriptionTab_OriginalMedication != null) {
                        medications.push(
                            <div key={id + "_child"} className="red-text">
                                {z.PrescriptionTab_OriginalMedication.GetNameWithStatus(prescription.Model.PrescriptionId, prescription.PrescriptionDate)}
                            </div>
                        );
                    }
                }
            });

            const printHistoryTooltipAnchorId = `patient-medication-tab-printed-by-${prescription.Model.PrescriptionId}`;
            const printHistoryElipsisedTextTooltipAnchorId = `patient-medication-tab-printed-by-ellipse-${prescription.Model.PrescriptionId}`;

            prescriptionRender.push(
                <div key={"__a" + idx} onMouseEnter={this.onMouseEnter.bind(this, prescription)}>
                    <div className="med-note" style={{ "paddingRight": "6px" }}>
                        <div className="tb">
                            <div className="tbc">
                                <p className="ellipsis-small"><strong>{prescription.Name}</strong></p>
                            </div>
                            <div className="tbc text-right">
                                <div
                                    className={`flex-container ${styles.providerNamePosition} ${isYourOwnWithNewMeds ? " pointer" : ""}`}
                                    onClick={this.toggleFavorite.bind(this, prescription.Model.PrescriptionId, isFavorite, isYourOwnWithNewMeds)}
                                >
                                    <div className="ellipsis-small" title={providerName}>
                                        <span>{providerName}</span>
                                    </div>
                                    {isFavorite && (
                                        <div style={{ "position": "absolute", "marginTop": "19px", "marginLeft": "1px" }}>
                                            <img style={{ "height": "14px", "verticalAlign": "top" }} id={prescription.Model.PrescriptionId + "_handle_med"} src={img_icon_favorite} />
                                        </div>
                                    )}
                                    {!isFavorite && isYourOwnWithNewMeds && (
                                        <div style={{ "position": "absolute", "marginTop": "19px", "marginLeft": "1px" }}>
                                            <img style={{ "height": "14px", "verticalAlign": "top" }} id={prescription.Model.PrescriptionId + "_handle_med"} src={img_icon_addfavorite} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {medications}
                        {cacheStore.ClinicSettings.EnableMedicationsPrintFilterAndHistory && prescription.Model.PrintHistory?.length > 0 && (
                            <div className={`flex-container ${styles.printHistory}`}>
                                <ContentWithOverflowTooltip
                                    place="right-start"
                                    id={printHistoryElipsisedTextTooltipAnchorId}
                                    renderContent={childProps => PrintHistory(prescription.Model.PrintHistory[0], childProps)}
                                />
                                {prescription.Model.PrintHistory?.length > 1 && (
                                    <>
                                        <small
                                            id={printHistoryTooltipAnchorId}
                                            className={styles.plusIndicator}
                                        >
                                            +{prescription.Model.PrintHistory.length - 1}
                                        </small>
                                        <Tooltip
                                            place="right-start"
                                            anchorSelect={`#${printHistoryTooltipAnchorId}`}
                                        >
                                            <List
                                                elementKey={printHistory => printHistory.Id}
                                                items={prescription.Model.PrintHistory.slice(1)}
                                                listElement={item => PrintHistory(item)}
                                            />
                                        </Tooltip>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        });

        return (
            <div data-hvar="0" className="note-collection scrollable">
                {prescriptionRender}
            </div>
        );
    }

    public openVigilanceAnalysis(): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicationTab.VigilanceAnalysis);
        LocalEventDispatcher.dispatch(operation);
    }

    private onMouseEnter(prescription: PatientPrescriptionListItem): void {
        var operation = new LocalEvent(LocalPatientOperations.MedicationTab.onPrescriptionMouseEnter);
        operation.AddItem("id", prescription.Model.PrescriptionId);
        LocalEventDispatcher.dispatch(operation);
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    private async toggleFavorite(prescriptionId: string, isFavorite: boolean, isYourOwn: boolean, e: React.MouseEvent): Promise<void> {
        e.stopPropagation();
        if (!isYourOwn)
            return;

        await cacheStore.requestToggleFavorite(prescriptionId, isFavorite, true);
        this.syncCacheStoreFavoritePrescriptions();
    }

    private syncCacheStoreFavoritePrescriptions(): void {
        this.mutateState(p => p.favoritePrescriptions = Immutable.List(CommonHelper.getCloneDeep(cacheStore.FavoritePrescriptions.toArray())));
    }
}

export { PatientMedicationTab };
